import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MessageCircle, Send, CheckCircle2, AlertTriangle, Clock, MousePointerClick, Zap, BellRing } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMEZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Kathmandu", "Asia/Dhaka",
  "Europe/London", "Europe/Berlin", "America/New_York", "America/Chicago",
  "America/Los_Angeles", "Australia/Sydney", "UTC",
];

const fmtRun = (iso) => {
  if (!iso) return "—";
  // Show the wall-clock time in the configured zone (taken straight from the ISO string)
  const [datePart, timePart] = iso.split("T");
  const day = new Date(`${datePart}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });
  return `${day} ${datePart.slice(5)} · ${timePart.slice(0, 5)}`;
};

function Pill({ ok, children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${
      ok ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
         : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {children}
    </span>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [s, setS] = useState(null);
  const [status, setStatus] = useState(null);
  const [adminNums, setAdminNums] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const loadStatus = () => {
    if (isAdmin) api.get("/alerts/twilio-status").then((r) => setStatus(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/settings").then((r) => {
      setS(r.data);
      setAdminNums((r.data.admin_whatsapp_numbers || []).join("\n"));
    });
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDay = (i) => {
    setS((cur) => ({
      ...cur,
      working_days: cur.working_days.includes(i)
        ? cur.working_days.filter((x) => x !== i)
        : [...cur.working_days, i].sort(),
    }));
  };

  const save = async () => {
    try {
      const payload = {
        ...s,
        admin_whatsapp_numbers: adminNums.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean),
      };
      const { data } = await api.patch("/settings", payload);
      setS(data);
      setAdminNums((data.admin_whatsapp_numbers || []).join("\n"));
      toast.success("Settings saved");
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a WhatsApp number"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/alerts/whatsapp-test", { to: testTo });
      if (data.status === "sent") toast.success(`Test message sent to ${data.to}`, { description: "Check WhatsApp — delivery can take a few seconds." });
      else if (data.status === "mocked") toast.message("Twilio not configured — test was only logged", { description: "Add TWILIO_* keys to backend/.env and restart." });
      else toast.error("Send failed", { description: data.error });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setTesting(false);
  };

  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const enableNotifications = async () => {
    if (typeof Notification === "undefined") { toast.error("This browser doesn't support notifications"); return; }
    const p = await Notification.requestPermission();
    setNotifPerm(p);
    if (p === "granted") {
      new Notification("EOD Tracker", { body: "Desktop alerts are on. You'll be alerted at reminder time." });
    } else toast.error("Notifications were blocked — allow them in the browser's site settings.");
  };
  const testLink = () => {
    const digits = (testTo || "").replace(/\D/g, "");
    const cc = s?.default_country_code || "91";
    const num = digits.length === 10 ? cc + digits : digits.replace(/^0+/, "");
    return `https://wa.me/${num}?text=${encodeURIComponent("✅ EOD Tracker test message — one-click WhatsApp works.")}`;
  };

  if (!s) return <div className="p-8 text-zinc-500">Loading…</div>;
  const click = (s.whatsapp_delivery || "click") === "click";

  const tzOptions = TIMEZONES.includes(s.timezone) ? TIMEZONES : [s.timezone, ...TIMEZONES];
  const field = "mt-1 bg-[#0f0f12] border-[#27272a]";
  const lbl = "text-xs uppercase tracking-wider text-zinc-400";

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Automation Config</div>
        <h1 className="font-display text-3xl font-bold">Settings</h1>
        <div className="text-sm text-zinc-400 mt-1">Cutoff times, working days, and daily WhatsApp notifications.</div>
      </div>

      {/* ---------- WhatsApp notifications ---------- */}
      <Card className="p-6 bg-[#121215] border-[#27272a]" data-testid="settings-whatsapp-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-500/15 text-emerald-300">
              <MessageCircle className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">Daily WhatsApp notifications</h2>
              <div className="text-xs text-zinc-500">
                {click ? "Every working day at the reminder time you get a pop-up to send reminders in one click."
                       : "Sent automatically every working day at the times below."}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">{s.whatsapp_enabled ? "On" : "Paused"}</span>
            <Switch checked={!!s.whatsapp_enabled} disabled={!isAdmin}
              data-testid="settings-whatsapp-enabled"
              onCheckedChange={(v) => setS({ ...s, whatsapp_enabled: v })} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-5" data-testid="settings-delivery">
          {[
            ["click", MousePointerClick, "One-click (free)", "Uses your own WhatsApp. At reminder time the app shows a list — click a name, WhatsApp opens with the message typed, press Send."],
            ["twilio", Zap, "Automatic (Twilio, paid)", "Messages go out by themselves through the Twilio WhatsApp API. Needs a Twilio account."],
          ].map(([val, Icon, title, desc]) => (
            <button key={val} type="button" disabled={!isAdmin}
              data-testid={`settings-delivery-${val}`}
              onClick={() => setS({ ...s, whatsapp_delivery: val })}
              className={`text-left p-4 rounded-lg border transition-colors ${
                (s.whatsapp_delivery || "click") === val
                  ? "bg-emerald-500/10 border-emerald-500/40"
                  : "bg-[#0f0f12] border-[#27272a] hover:border-[#3f3f46]"}`}>
              <div className="flex items-center gap-2 font-medium text-sm">
                <Icon className="w-4 h-4 text-emerald-300" /> {title}
                {(s.whatsapp_delivery || "click") === val && <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />}
              </div>
              <div className="text-xs text-zinc-500 mt-1">{desc}</div>
            </button>
          ))}
        </div>

        {isAdmin && click && status && (
          <div className="flex flex-wrap gap-2 mb-5">
            <Pill ok={status.managers_reachable === status.managers_active}>
              {status.managers_reachable}/{status.managers_active} managers have a WhatsApp number
            </Pill>
            <Pill ok={notifPerm === "granted"}>{notifPerm === "granted" ? "Desktop alerts on" : "Desktop alerts off"}</Pill>
          </div>
        )}
        {isAdmin && !click && status && (
          <div className="flex flex-wrap gap-2 mb-5" data-testid="whatsapp-status">
            <Pill ok={status.enabled}>{status.enabled ? `Twilio connected${status.sandbox ? " (sandbox)" : ""}` : "Twilio not configured — messages are only logged"}</Pill>
            {status.enabled && !status.sandbox && (
              <Pill ok={status.templates?.reminder}>{status.templates?.reminder ? "Reminder template set" : "No approved reminder template"}</Pill>
            )}
            <Pill ok={status.managers_reachable === status.managers_active}>
              {status.managers_reachable}/{status.managers_active} managers reachable
            </Pill>
            <Pill ok={status.admin_recipients > 0}>{status.admin_recipients} admin recipient(s)</Pill>
          </div>
        )}
        {isAdmin && status?.managers_missing_number?.length > 0 && (
          <div className="text-xs text-amber-300/90 mb-5 -mt-2">
            No WhatsApp number for: {status.managers_missing_number.join(", ")} — add it on the Managers page.
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className={lbl}>Timezone</Label>
            <Select value={s.timezone} disabled={!isAdmin}
              onValueChange={(v) => setS({ ...s, timezone: v })}>
              <SelectTrigger data-testid="settings-timezone" className={field}><SelectValue /></SelectTrigger>
              <SelectContent>
                {tzOptions.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-xs text-zinc-500 mt-1">
              All times below are in this zone{status?.local_time ? ` · now ${status.local_time.slice(11)}` : ""}.
            </div>
          </div>
          <div>
            <Label className={lbl}>Default country code</Label>
            <Input value={s.default_country_code} disabled={!isAdmin}
              data-testid="settings-country-code"
              onChange={(e) => setS({ ...s, default_country_code: e.target.value.replace(/\D/g, "") })}
              className={field} />
            <div className="text-xs text-zinc-500 mt-1">Added to 10-digit numbers (91 = India).</div>
          </div>

          <div className="p-4 rounded-lg bg-[#0f0f12] border border-[#27272a]">
            <Label className={lbl}>Manager reminder time</Label>
            <Input type="time" value={s.whatsapp_reminder_time} disabled={!isAdmin}
              data-testid="settings-reminder-time-input"
              onChange={(e) => setS({ ...s, whatsapp_reminder_time: e.target.value })}
              className={field} />
            <div className="text-xs text-zinc-500 mt-2">
              {click
                ? "At this time the EOD Tracker pops up a list of everyone who hasn't submitted, so you can send each reminder with one click."
                : "Every manager who hasn't submitted today's EOD gets a WhatsApp reminder."}
            </div>
            {!click && status?.next_reminder && (
              <div className="text-xs text-zinc-400 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Next: {fmtRun(status.next_reminder)}</div>
            )}
          </div>

          {click ? (
            <div className="p-4 rounded-lg bg-[#0f0f12] border border-[#27272a]">
              <Label className={lbl}>Desktop alert</Label>
              <div className="text-xs text-zinc-500 mt-2 mb-3">
                Get a computer notification at reminder time, even when the EOD Tracker tab is in the background.
                (The tab must stay open in your browser.)
              </div>
              <Button type="button" variant="outline" onClick={enableNotifications}
                data-testid="enable-desktop-alerts" disabled={notifPerm === "granted"}
                className="border-[#27272a]">
                <BellRing className="w-4 h-4 mr-2" /> {notifPerm === "granted" ? "Desktop alerts are on" : "Turn on desktop alerts"}
              </Button>
            </div>
          ) : (
          <div className="p-4 rounded-lg bg-[#0f0f12] border border-[#27272a]">
            <div className="flex items-center justify-between">
              <Label className={lbl}>Admin digest time</Label>
              <Switch checked={!!s.whatsapp_digest_enabled} disabled={!isAdmin}
                data-testid="settings-digest-enabled"
                onCheckedChange={(v) => setS({ ...s, whatsapp_digest_enabled: v })} />
            </div>
            <Input type="time" value={s.whatsapp_digest_time} disabled={!isAdmin || !s.whatsapp_digest_enabled}
              data-testid="settings-digest-time-input"
              onChange={(e) => setS({ ...s, whatsapp_digest_time: e.target.value })}
              className={field} />
            <div className="text-xs text-zinc-500 mt-2">Admins get a summary of who missed today's EOD.</div>
            {status?.next_digest && (
              <div className="text-xs text-zinc-400 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Next: {fmtRun(status.next_digest)}</div>
            )}
          </div>
          )}

          {!click && (<>
          <div>
            <Label className={lbl}>Admin WhatsApp numbers</Label>
            <Textarea rows={3} value={adminNums} disabled={!isAdmin}
              placeholder={"+91 98xxx xxxxx\n+91 97xxx xxxxx"}
              data-testid="settings-admin-numbers"
              onChange={(e) => setAdminNums(e.target.value)}
              className={`${field} font-mono text-xs`} />
            <div className="text-xs text-zinc-500 mt-1">One per line. Receive the daily digest and escalations.</div>
          </div>
          <div>
            <Label className={lbl}>Escalate after missed days</Label>
            <Input type="number" min={1} max={30} value={s.escalation_missed_threshold} disabled={!isAdmin}
              data-testid="settings-escalation-threshold"
              onChange={(e) => setS({ ...s, escalation_missed_threshold: Number(e.target.value) })}
              className={field} />
            <div className="text-xs text-zinc-500 mt-1">Admins are alerted when a manager misses this many working days in a row.</div>
          </div>
          </>)}
        </div>

        {isAdmin && (
          <div className="mt-5 pt-5 border-t border-[#27272a]">
            <Label className={lbl}>Send a test message</Label>
            <div className="flex gap-2 mt-1">
              <Input value={testTo} placeholder="+91 98xxx xxxxx"
                data-testid="whatsapp-test-number"
                onChange={(e) => setTestTo(e.target.value)}
                className="bg-[#0f0f12] border-[#27272a]" />
              {click ? (
                <Button asChild variant="outline"
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 shrink-0">
                  <a href={testLink()} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-test-button"
                    onClick={(e) => { if (!testTo.trim()) { e.preventDefault(); toast.error("Enter a WhatsApp number"); } }}>
                    <Send className="w-4 h-4 mr-2" /> Open WhatsApp
                  </a>
                </Button>
              ) : (
              <Button onClick={sendTest} disabled={testing} variant="outline"
                data-testid="whatsapp-test-button"
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 shrink-0">
                <Send className="w-4 h-4 mr-2" /> {testing ? "Sending…" : "Send test"}
              </Button>
              )}
            </div>
            {click && (
              <div className="text-xs text-zinc-500 mt-2">
                Opens WhatsApp (app or WhatsApp Web) with a test message typed in. Press Send to check it works.
              </div>
            )}
            {!click && status?.sandbox && (
              <div className="text-xs text-zinc-500 mt-2">
                Sandbox: each recipient must first send the "join &lt;code&gt;" message to +1 415 523 8886 on WhatsApp.
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-6 bg-[#121215] border-[#27272a]">
        <h2 className="font-display text-lg font-semibold mb-4">Timing</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className={lbl}>Cutoff hour (next day)</Label>
            <Input type="number" min={0} max={23} value={s.cutoff_hour}
              data-testid="settings-cutoff-time-input"
              onChange={(e) => setS({ ...s, cutoff_hour: Number(e.target.value) })}
              disabled={!isAdmin} className={field} />
            <div className="text-xs text-zinc-500 mt-1">Reports received before {s.cutoff_hour}:00 UTC count for the previous day.</div>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-[#121215] border-[#27272a]">
        <h2 className="font-display text-lg font-semibold mb-1">Working days</h2>
        <div className="text-xs text-zinc-500 mb-4">WhatsApp notifications are only sent on these days.</div>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d, i) => {
            const active = s.working_days.includes(i);
            return (
              <button key={d} onClick={() => isAdmin && toggleDay(i)}
                data-testid={`settings-day-${d.toLowerCase()}`}
                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                  active
                    ? "bg-indigo-500/15 border-indigo-500/40 text-indigo-200"
                    : "bg-[#0f0f12] border-[#27272a] text-zinc-500"
                }`}>
                {d}
              </button>
            );
          })}
        </div>
      </Card>

      <Card className="p-6 bg-[#121215] border-[#27272a]">
        <h2 className="font-display text-lg font-semibold mb-1">WhatsApp templates</h2>
        <div className="text-xs text-zinc-500 mb-4">
          Used for the Twilio sandbox and replies within 24h. For production, WhatsApp requires an
          approved template — set its Content SID in backend/.env and it will be used instead.
        </div>
        <div className="space-y-4">
          <div>
            <Label className={lbl}>Reminder to manager</Label>
            <Textarea rows={3} value={s.whatsapp_template_reminder}
              data-testid="settings-template-reminder"
              onChange={(e) => setS({ ...s, whatsapp_template_reminder: e.target.value })}
              disabled={!isAdmin}
              className={`${field} font-mono text-xs`} />
            <div className="text-xs text-zinc-500 mt-1">Variables: {"{name}"}, {"{date}"}</div>
          </div>
          <div>
            <Label className={lbl}>Admin daily digest</Label>
            <Textarea rows={3} value={s.whatsapp_template_admin_digest}
              data-testid="settings-template-digest"
              onChange={(e) => setS({ ...s, whatsapp_template_admin_digest: e.target.value })}
              disabled={!isAdmin}
              className={`${field} font-mono text-xs`} />
            <div className="text-xs text-zinc-500 mt-1">Variables: {"{date}"}, {"{missed_count}"}, {"{missed_names}"}</div>
          </div>
          <div>
            <Label className={lbl}>Escalation to admins</Label>
            <Textarea rows={3} value={s.whatsapp_template_escalation}
              data-testid="settings-template-escalation"
              onChange={(e) => setS({ ...s, whatsapp_template_escalation: e.target.value })}
              disabled={!isAdmin}
              className={`${field} font-mono text-xs`} />
            <div className="text-xs text-zinc-500 mt-1">Variables: {"{name}"}, {"{streak}"}, {"{last_date}"}</div>
          </div>
        </div>
      </Card>

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={save} data-testid="settings-save-button"
            className="bg-indigo-500 hover:bg-indigo-600 text-white">Save changes</Button>
        </div>
      )}
    </div>
  );
}
